import * as React from "react";
import { graphql } from "react-apollo";
import { Helmet } from "react-helmet";
import {
  FormGroup,
  Input,
  Label,
  Button,
  InputGroup,
  InputGroupAddon
} from "reactstrap";
import { TiLockClosedOutline } from "react-icons/ti";
import { Link, withRouter } from "react-router-dom";
import { Redirect, RouteComponentProps } from "react-router";
import styled from "@emotion/styled";
import * as jwtDecode from "jwt-decode";

// GraphQL
import VerifyUserEmailAndSetPasswordMutation from "../../GraphQL/Mutations/VerifyUserEmailAndSetPassword";

// Utils
import { validateToken } from "../../Utils/Helpers";

// Presentation/UI
import LottieWrapper from "../../Components/Anim/LottieWrapper";

// Theme
import { Colors } from "../../Themes";

interface IPathParams {
  token: string;
}

type PropsType = RouteComponentProps<IPathParams> & {
  computedMatch: {
    params: {
      token: string;
    };
  };
  mutate: any;
};

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  background: ${Colors.snow};
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-size: cover;
  background-position: 50% 50%;
  z-index: -1;
  padding: 0;
  margin: 0;
  flex-direction: column;
`;

class UserInvitation extends React.Component<PropsType> {
  state = {
    verified: false,
    loading: false,
    password: "",
    passwordRetyped: "",
    error: null
  };

  // Render view
  renderView = (): React.ReactNode => {
    const { loading, verified, password, passwordRetyped } = this.state;

    if (loading) {
      return (
        <div>
          <LottieWrapper
            loop={true}
            width={140}
            height={140}
            anim={require("../../LottieFiles/loading_circle.json")}
          />
          <br />
          <br />
          <p className="center-align">Please wait...</p>
        </div>
      );
    }

    if (verified) {
      return (
        <div>
          <LottieWrapper
            loop={false}
            width={220}
            height={220}
            anim={require("../../LottieFiles/success1.json")}
          />
          <br />
          <br />
          <p className="center-align">
            Your password has been set successfully!
          </p>
          <br />
          <Link to="/sign-in">
            <Button>Go to Login</Button>
          </Link>
        </div>
      );
    }

    return (
      <div style={{ width: "400px" }}>
        <FormGroup>
          <Label for="userPassword">Password</Label>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <TiLockClosedOutline size="1.5em" />
            </InputGroupAddon>
            <Input
              type="password"
              name="password"
              value={password}
              id="userPassword"
              placeholder="Password"
              onChange={this.handleChange}
            />
          </InputGroup>
        </FormGroup>
        <FormGroup>
          <Label for="userPasswordRetyped">Retype Password</Label>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <TiLockClosedOutline size="1.5em" />
            </InputGroupAddon>
            <Input
              type="password"
              name="passwordRetyped"
              value={passwordRetyped}
              id="userPasswordRetyped"
              placeholder="Retype Password"
              onChange={this.handleChange}
            />
          </InputGroup>
        </FormGroup>
        {this.state.error && (
          <p style={{ textAlign: "center", color: Colors.error }}>
            {this.state.error}
          </p>
        )}
        <br />
        <Button onClick={() => this.verifyUserEmailAddress()}>
          Set Password
        </Button>
      </div>
    );
  };

  // Handle change
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "password":
        this.setState({ password: value });
        break;
      case "passwordRetyped":
        this.setState({ passwordRetyped: value });
        break;
      default:
        break;
    }
  };

  // Verify user email
  verifyUserEmailAddress = (): any => {
    const { computedMatch } = this.props;
    const { password, passwordRetyped } = this.state;

    // validate passwords
    if (!passwordRetyped || !password) {
      this.setError("Please fill in both fields");
      return false;
    }

    if (passwordRetyped !== password) {
      this.setError("Please ensure that the passwords match");
      return false;
    }

    this.setState({ loading: true });
    // Decode token
    const decodedJwt: any = jwtDecode(computedMatch.params.token);

    this.props
      .mutate({
        variables: {
          id: decodedJwt.userId,
          password
        }
      })
      .then((res: any) => {
        // Handle successful response
        this.setState({
          loading: false,
          verified: true
        });
      })
      .catch((err: any) => {
        // Do something with error message
        this.setState({ loading: false, password: "", passwordRetyped: "" });
        this.setError("Oops, something went wrong. Please try again.");
      });
  };

  // Error
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  render() {
    const { computedMatch } = this.props;
    // redirect if the token is invalid
    if (!validateToken(computedMatch.params.token)) {
      return <Redirect to="/" />;
    }

    return (
      <Wrapper>
        {this.renderView()}
        <Helmet title="User Invitation" />
      </Wrapper>
    );
  }
}

export default graphql(VerifyUserEmailAndSetPasswordMutation)(
  withRouter(UserInvitation)
);



// WEBPACK FOOTER //
// ./src/Containers/UserInvitation/index.tsx